import { Subject } from 'rxjs';
import { getSumOfObjectProps } from '../../../utilities/object-math-utility/object-math-utility';
import {ColWidths, ComponentConfig, CurrentOffsetAndColspan, DataHeader, DataIndex, LookUpTableData, SortImageType} from '../data-table-display';
import {RequestObjContainer} from 'picker-sdk';
import {ReportJSONDataResponse, ReportJSONStaticDataResponse} from '../../../core/dtos/api';

export abstract class ParentReportDataManagerEdw3 {
  public isReady = false;
  public isReadyToDisplay = false;
  public hasFacts: boolean;
  public allowNoFacts: boolean;
  public sortedHeader: { [index: string]: boolean };
  public sortedPartition: { [index: string]: boolean };
  public rowsInTile: number;
  public partitionsInTile: number;
  public partitionOffsetOfInitialRequest: number;
  public sortIcon: SortImageType;
  public totalPartitionsInReport: number;
  public totalRowsInReport: number;
  public partitionKeyList: string[];
  public partitionTitles: string[];
  public lookUpTableData: LookUpTableData[];
  public reportHasData = true;
  public posPartitionTile: number;
  public lastPosPartitionTile: number;
  public posRowTile: number;
  public lastPosRowTile: number;
  public colspanWidths: number[] = [];
  public totalHeaderLookupsForNgFor: number[];
  public dimColWidths: ColWidths = {};
  public factColWidths: ColWidths = {};
  public factColSpan: number;
  public currentDisplayPositionTop: number;
  public verticalScrollTop: number;
  public currentFactDisplayPositionLeft: number;
  public factHorizontalScrollLeft: number;
  public posPartitionCalculated: number;
  public posRowCalculated: number;
  public dataTableRowHeight: number;
  public hasCustomComponents: boolean;
  public isCancelled: boolean;

  public errorSubject: Subject<string>;
  public warningSubject: Subject<string[]>;
  public progressSpinnerSubject: Subject<boolean>;
  public dataCompleteSubject: Subject<boolean>;
  public sortedRequestSubject: Subject<boolean>;
  public checkForColSpanSubject: Subject<number>;
  public noDataReturnedSubject: Subject<RequestObjContainer>;
  public scrollHasChanged: Subject<string>;
  public triggerChangeDetectionSubject: Subject<boolean>;

  protected partitionDepth: number;
  protected endPartitionTile: number;
  protected endRowTile: number;
  protected componentConfig: ComponentConfig[];
  protected readonly DEFAULT_ROW_HEIGHT: number = 20;


  constructor() {
  }

  public scrollHasUpdated(displayName: string): void {
    this.scrollHasChanged.next(displayName);
  }

  protected addColSpansToLookUpArray(offsetAndColspanLookup: CurrentOffsetAndColspan[]): void {
    let workingColumn: number = (this.lookUpTableData[this.partitionKeyList[this.partitionOffsetOfInitialRequest]].facts.length * this.partitionKeyList.length);

    for (let lastPiecesIndex = 0; lastPiecesIndex < this.partitionDepth; lastPiecesIndex++) {
      offsetAndColspanLookup[lastPiecesIndex].colspan = workingColumn - offsetAndColspanLookup[lastPiecesIndex].offset;
      this.lookUpTableData[this.partitionKeyList[this.partitionKeyList.length - 1]].headerLookups[lastPiecesIndex].colspan
        = workingColumn - offsetAndColspanLookup[lastPiecesIndex].offset;
    }

    for (let partitionKeyIndex = (this.partitionKeyList.length - 1); partitionKeyIndex >= 0; partitionKeyIndex--) {
      for (let headerLookUpObjIndex = 0; headerLookUpObjIndex < this.lookUpTableData[this.partitionKeyList[partitionKeyIndex]].headerLookups.length;
        headerLookUpObjIndex++) {
        if (this.lookUpTableData[this.partitionKeyList[partitionKeyIndex]].headerLookups[headerLookUpObjIndex].name
          === offsetAndColspanLookup[headerLookUpObjIndex].name
          && headerLookUpObjIndex !== (this.partitionDepth - 1)) {
        } else {
          offsetAndColspanLookup[headerLookUpObjIndex].name
            = this.lookUpTableData[this.partitionKeyList[partitionKeyIndex]].headerLookups[headerLookUpObjIndex].name;
          offsetAndColspanLookup[headerLookUpObjIndex].colspan = workingColumn
            - this.lookUpTableData[this.partitionKeyList[partitionKeyIndex]].headerLookups[headerLookUpObjIndex].offset;
        }
        this.lookUpTableData[this.partitionKeyList[partitionKeyIndex]].headerLookups[headerLookUpObjIndex].colspan =
          offsetAndColspanLookup[headerLookUpObjIndex].colspan;
      }
      workingColumn -= this.lookUpTableData[this.partitionKeyList[this.partitionOffsetOfInitialRequest]].facts.length;
    }
  }

  protected addToLookUpTableData(reportData: ReportJSONDataResponse[], partitionKey: string[] = [], dataRowListOffset: number): boolean {
    let addedPartition = false;
    let focusedPartition = this.getFocusedPartition(partitionKey, reportData);
    let lookUpTableDataKeyName = partitionKey.join('||');
    if (!focusedPartition.data) {
      focusedPartition.data = [];
    }
    if (this.lookUpTableData[lookUpTableDataKeyName]) {
      for (let focusedPartitionDataIndex = 0; focusedPartitionDataIndex < focusedPartition.data.length; focusedPartitionDataIndex++) {
        this.lookUpTableData[lookUpTableDataKeyName].data[dataRowListOffset + focusedPartitionDataIndex] = focusedPartition.data[focusedPartitionDataIndex];
      }
    } else {
      addedPartition = true;
      this.lookUpTableData[String(lookUpTableDataKeyName)] = {};
      this.lookUpTableData[lookUpTableDataKeyName].headerLookups = [];
      this.lookUpTableData[lookUpTableDataKeyName].partition = focusedPartition.partition;
      this.lookUpTableData[lookUpTableDataKeyName].alias = focusedPartition.alias;
      this.lookUpTableData[lookUpTableDataKeyName].dims = [];

      // This data will only be added in cases where this is staticData.
      if ((focusedPartition as ReportJSONStaticDataResponse).sortInfo) {
        this.lookUpTableData[lookUpTableDataKeyName].sortInfo
          = (focusedPartition as ReportJSONStaticDataResponse).sortInfo;
      }

      if ((focusedPartition as ReportJSONStaticDataResponse).primarySortCol !== undefined) {
        this.lookUpTableData[lookUpTableDataKeyName].primarySortCol
          = (focusedPartition as ReportJSONStaticDataResponse).primarySortCol;
      }

      focusedPartition.dims.forEach((value) => {
        this.lookUpTableData[lookUpTableDataKeyName].dims.push({
          index: value,
          isComponent: false
        });
      });
      this.lookUpTableData[lookUpTableDataKeyName].facts = [];
      focusedPartition.facts.forEach((value) => {
        this.lookUpTableData[lookUpTableDataKeyName].facts.push({
          index: value,
          isComponent: false
        });
      });

      this.componentConfig.forEach((singleConfig) => {
        let componentItem: DataIndex = {
          index: singleConfig.associatedDataIndex,
          component: singleConfig.component,
          isComponent: true,
          textAlign: (singleConfig.textAlign ? singleConfig.textAlign : '')
        };
        if (singleConfig.isDim) {
          if (singleConfig.position > this.lookUpTableData[lookUpTableDataKeyName].dims.length) {
            this.lookUpTableData[lookUpTableDataKeyName].dims.push(componentItem);
          } else {
            this.lookUpTableData[lookUpTableDataKeyName].dims.splice(singleConfig.position, 0, componentItem);
          }
        } else {
          if (singleConfig.position > this.lookUpTableData[lookUpTableDataKeyName].facts.length) {
            this.lookUpTableData[lookUpTableDataKeyName].facts.push(componentItem);
          } else {
            this.lookUpTableData[lookUpTableDataKeyName].facts.splice(singleConfig.position, 0, componentItem);
          }
        }
      });
      let dims = this.lookUpTableData[lookUpTableDataKeyName].dims;
      let facts = this.lookUpTableData[lookUpTableDataKeyName].facts;

      let headers: DataHeader[][] = this.addColSpansToHeaders(focusedPartition.headers, dims, facts);
      this.lookUpTableData[lookUpTableDataKeyName].headers = headers;
      this.lookUpTableData[lookUpTableDataKeyName].partitionHeader = focusedPartition.partitionHeader;
      if (focusedPartition.totals) {
        this.lookUpTableData[lookUpTableDataKeyName].totals = focusedPartition.totals;
      }
      if (focusedPartition.hidden) {
        this.lookUpTableData[lookUpTableDataKeyName].hidden = focusedPartition.hidden;
      }

      // add row data
      this.lookUpTableData[lookUpTableDataKeyName].data = [];
      for (let focusedPartitionDataIndex = 0; focusedPartitionDataIndex < focusedPartition.data.length; focusedPartitionDataIndex++) {
        this.lookUpTableData[lookUpTableDataKeyName].data[dataRowListOffset + focusedPartitionDataIndex] = focusedPartition.data[focusedPartitionDataIndex];
      }
    }
    return addedPartition;
  }

  protected getPartitionTitles(reportData: ReportJSONDataResponse[]): void {
    this.partitionTitles = [];
    let getPartitionTitleFromSlice = (partitionHeaderTitle: string, partitionSlice: ReportJSONDataResponse) => {
      if (partitionHeaderTitle && partitionHeaderTitle !== 'Default') {
        this.partitionTitles.push(partitionHeaderTitle);
      } else {
        return;
      }
      if (partitionSlice.slices && partitionSlice.slices.length > 0) {
        getPartitionTitleFromSlice(partitionSlice.slices[0].partitionHeader, partitionSlice.slices[0]);
      }
    };
    getPartitionTitleFromSlice(reportData[0].partitionHeader, reportData[0]);
  }

  protected setReportLimits(): void {
    this.endPartitionTile = Math.floor((this.totalPartitionsInReport - 1) / this.partitionsInTile);
    this.endRowTile = Math.floor((this.totalRowsInReport - 1) / this.rowsInTile);
  }

  protected getMaxPartitionHeaderLength(): number {
    // This gets the longest title and returns the length of that title.
    return this.partitionTitles.reduce((a, b): string => a.length > b.length ? a : b, '').length;
  }

  protected maybeAddColsLength(colWidths: ColWidths, partitionLength: number): ColWidths {
    let tempColWidths = JSON.parse(JSON.stringify(colWidths));
    let totalColLength = getSumOfObjectProps(tempColWidths);
    // Compare passed partition length to total passed col width. If partition length is longer
    // take the difference and add it equally across all col widths.
    if (totalColLength < partitionLength) {
      let tempColWidthsKeys = Object.keys(tempColWidths);
      let lengthDiff = partitionLength - totalColLength;
      let addAmount = Math.ceil(lengthDiff / tempColWidthsKeys.length);
      for (let tempColWidthsKey of tempColWidthsKeys) {
        tempColWidths[tempColWidthsKey] += addAmount;
      }
    }
    return tempColWidths;
  }

  private getFocusedPartition(partitionKey: string[], reportData: ReportJSONDataResponse[]): ReportJSONDataResponse | ReportJSONStaticDataResponse {
    let resultingPartition: ReportJSONDataResponse;
    let hiddenPartitionValues = partitionKey.slice(this.partitionDepth);
    let currentFoundPartitions: ReportJSONDataResponse[] = reportData;
    let futureFoundPartitions: ReportJSONDataResponse[];

    // Check for partitions with only hidden partitions.
    if (this.partitionDepth > 1 || partitionKey[0] !== undefined) {
      // This gets matching partitions by visible partitions.
      for (let partitionDepthIndex = 0; partitionDepthIndex < this.partitionDepth; partitionDepthIndex++) {
        futureFoundPartitions = currentFoundPartitions.filter(({value}) => value === partitionKey[partitionDepthIndex]);
        if (futureFoundPartitions[0].slices) {
          currentFoundPartitions = [];
          for (let futureFoundPartition of futureFoundPartitions) {
            for (let futureFoundPartitionSlice of futureFoundPartition.slices) {
              currentFoundPartitions.push(futureFoundPartitionSlice);
            }
          }
        } else {
          currentFoundPartitions = futureFoundPartitions.slice();
        }
      }
    }

    // If there are multiple matches we will need to check the hidden attribute.
    if (currentFoundPartitions.length === 1) {
      resultingPartition = currentFoundPartitions[0];
    } else {
      for (let currentFoundPartition of currentFoundPartitions) {
        let isMatch = true;
        for (let hiddenPartitionIndex = 0; hiddenPartitionIndex < hiddenPartitionValues.length; hiddenPartitionIndex++) {
          if (currentFoundPartition.hidden[Object.keys(currentFoundPartition.hidden)[hiddenPartitionIndex]] !== hiddenPartitionValues[hiddenPartitionIndex]) {
            isMatch = false;
            break;
          }
        }
        if (isMatch) {
          resultingPartition = currentFoundPartition;
          break;
        }
      }
    }

    return resultingPartition;
  }

  private addColSpansToHeaders(headers: string[] | string[][], dims: DataIndex[], facts: DataIndex[]): DataHeader[][] {
    let returnHeaders: DataHeader[][] = [];
    // If the report does not have sub-headers just shape the single headers.
    if (!Array.isArray(headers[0])) {
      (headers as string[]).forEach((singleHeader, index) => {
        let singleItem = {
          name: singleHeader,
          colspan: 1
        };
        returnHeaders[index] = [singleItem];
      });
    } else {
      // This finds the number of sub-headers for each header.
      let lengthOfInnerArray = headers[0].length;
      // Loop over the depth of the header/sub-headers.
      for (let innerArrayIndex = 0; innerArrayIndex < lengthOfInnerArray; innerArrayIndex++) {
        // Go over each dim.
        for (let dimArrayIndex = 0; dimArrayIndex < dims.length; dimArrayIndex++) {
          let currentDataArrayIndexBeingChecked = dimArrayIndex;
          // We want to check the next header at this position.
          currentDataArrayIndexBeingChecked++;
          let colSpan = 1;
          let singleItem: DataHeader = {
            name: headers[dims[dimArrayIndex].index][innerArrayIndex],
            colspan: 1
          };
          // If right neighbor is the same header name increase colspan.
          for (currentDataArrayIndexBeingChecked; currentDataArrayIndexBeingChecked < dims.length; currentDataArrayIndexBeingChecked++) {
            if ((headers[dims[dimArrayIndex].index][innerArrayIndex] !== headers[dims[currentDataArrayIndexBeingChecked].index][innerArrayIndex])) {
              break;
            }
            colSpan++;
          }
          singleItem.colspan = colSpan;
          returnHeaders[dims[dimArrayIndex].index] = returnHeaders[dims[dimArrayIndex].index] ? returnHeaders[dims[dimArrayIndex].index] : [];
          returnHeaders[dims[dimArrayIndex].index][innerArrayIndex] = singleItem;
        }
        // Go over each fact.
        for (let factArrayIndex = 0; factArrayIndex < facts.length; factArrayIndex++) {
          let currentDataArrayIndexBeingChecked = factArrayIndex;
          // We want to check the next header at this position.
          currentDataArrayIndexBeingChecked++;
          let colSpan = 1;
          let singleItem: DataHeader = {
            name: headers[facts[factArrayIndex].index][innerArrayIndex],
            colspan: 1
          };
          // If right neighbor is the same header name increase colspan.
          for (currentDataArrayIndexBeingChecked; currentDataArrayIndexBeingChecked < facts.length; currentDataArrayIndexBeingChecked++) {
            if ((headers[facts[factArrayIndex].index][innerArrayIndex] !== headers[facts[currentDataArrayIndexBeingChecked].index][innerArrayIndex])) {
              break;
            }
            colSpan++;
          }
          singleItem.colspan = colSpan;
          returnHeaders[facts[factArrayIndex].index] = returnHeaders[facts[factArrayIndex].index] ? returnHeaders[facts[factArrayIndex].index] : [];
          returnHeaders[facts[factArrayIndex].index][innerArrayIndex] = singleItem;
        }
      }
    }
    return returnHeaders;
  }
}
